import React, { useEffect, useMemo, useRef, useState } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import KeyboardTooltip from './KeyboardTooltip';
import NoBeacon from './NoBeacon';
import { setKeyboardWalkthrough, setVoiceWalkthrough } from '../../Services/userSettings';

/**
 * ArisTour - reusable Joyride v2 wrapper with controlled mode, per-step beacon disable, and route/storage gating.
 *
 * Props:
 * - id: string unique id (e.g., 'keyboard', 'voice')
 * - steps: Array<{ selector: string, content: React.ReactNode, placement?: string }>
 * - routeMatch?: (pathname: string) => boolean (defaults to always true)
 * - readySelectors?: string[] (optional list to wait for before starting; defaults to first step selector)
 * - primaryColor?: string (defaults to #1E69FF)
 * - zIndex?: number (defaults to 10000)
 */
export default function ArisTour({
  id,
  steps,
  routeMatch = () => true,
  readySelectors,
  primaryColor = '#1E69FF',
  zIndex = 10000,
  onTourComplete,
  spotlightRadiusMap = {}, // e.g., { '.rl-input': 12, '.resume-coaching-icon-container': 44 }
  defaultSpotlightRadius = 44,
  spotlightPaddingMap = {}, // e.g., { '.rl-input': 0, '.some-circle': 8 }
  defaultSpotlightPadding = 0,
}) {
  const TOUR_STORAGE_KEY = `${id}_walkthrough_done`;
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const forceTour = !!urlParams && urlParams.get('tour') === id;
  const debugTour = !!urlParams && (urlParams.get('debugTour') === '1' || urlParams.get('debugTour') === id);

  const [run, setRun] = useState(false);
  const [idx, setIdx] = useState(0);
  const [joyrideSteps, setJoyrideSteps] = useState([]);
  const helpersRef = useRef(null);
  const startedRef = useRef(false);
  const isStartedGlobally = () => {
    try { return !!(window && window.__aris_tour_started && window.__aris_tour_started[id]); } catch { return false; }
  };
  const markStarted = () => {
    startedRef.current = true;
    try {
      window.__aris_tour_started = window.__aris_tour_started || {};
      window.__aris_tour_started[id] = true;
    } catch {}
  };

  const isRoute = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try { return routeMatch(window.location.pathname); } catch { return false; }
  }, [routeMatch]);

  // Prepare steps with per-step disableBeacon flag
  const baseSteps = useMemo(() => {
    return (steps || []).map(s => ({
      target: s.selector,
      content: s.content,
      placement: s.placement || 'top',
      disableBeacon: true,
    }));
  }, [steps]);

  // Wait for DOM readiness for the first target
  useEffect(() => {
    if (!isRoute) return;
    if (!forceTour && (startedRef.current || run || isStartedGlobally())) return; // already started this session (unless forced)
    let done = false;
    try { done = localStorage.getItem(TOUR_STORAGE_KEY) === 'true'; } catch {}
    if (forceTour) done = false;
    if (done) return;

    setJoyrideSteps(baseSteps);

    const selectors = readySelectors && readySelectors.length > 0
      ? readySelectors
      : (baseSteps[0]?.target ? [baseSteps[0].target] : []);

    const tryStart = (attempts = 0) => {
      const found = selectors.find((sel) => sel && document.querySelector(sel));
      if (found) {
        // Ensure step 0 points to the valid selector we found
        setJoyrideSteps(prev => {
          const next = [...prev];
          if (next[0]) next[0] = { ...next[0], target: found };
          return next;
        });
        setIdx(0);
        setRun(true);
        markStarted();
      } else if (attempts < 20) {
        setTimeout(() => tryStart(attempts + 1), 150);
      } else {
        // Fallback to body center so the tour is visible in force mode
        setJoyrideSteps(prev => {
          const next = [...prev];
          if (next[0]) next[0] = { ...next[0], target: 'body', placement: 'center' };
          return next;
        });
        setIdx(0);
        setRun(true);
        markStarted();
      }
    };

    tryStart(0);
  }, [isRoute, baseSteps, forceTour, readySelectors]);

  // Jump to current step when helpers are ready
  useEffect(() => {
    if (run && helpersRef.current) {
      try { helpersRef.current.go(idx || 0); } catch {}
    }
  }, [run, idx]);

  // Dynamically adjust spotlight border-radius based on current target
  useEffect(() => {
    if (!run) return;
    const step = joyrideSteps[idx];
    if (!step || !step.target) return;

    let observer;
    let rafId = 0;
    let cancelled = false;

    const getSpotlightEl = () => document.querySelector('.react-joyride__spotlight') || document.querySelector('[data-test-id="spotlight"]');

    const computeAndApply = () => {
      try {
        const targetEl = document.querySelector(step.target);
        const spotlightEl = getSpotlightEl();
        if (!spotlightEl || !targetEl) return false;

        // 1) Explicit mapping by selector
        let radius = Object.entries(spotlightRadiusMap || {}).find(([sel]) => {
          try { return !!document.querySelector(sel) && targetEl && targetEl.matches(sel); } catch { return false; }
        });
        radius = Array.isArray(radius) ? Number(radius[1]) : NaN;

        // 2) Infer from target element style if no explicit radius
        if (!Number.isFinite(radius) || radius <= 0) {
          const cs = window.getComputedStyle(targetEl);
          const br = parseFloat(cs.borderTopLeftRadius || cs.borderRadius || '0');
          const w = parseFloat(cs.width || '0');
          const h = parseFloat(cs.height || '0');
          if (w && h && Math.abs(w - h) < 4 && (br >= Math.min(w, h) / 2 - 2)) {
            radius = Math.round(Math.min(w, h) / 2);
          } else if (br) {
            radius = Math.round(br);
          }
        }

        if (!Number.isFinite(radius) || radius <= 0) radius = Number(defaultSpotlightRadius) || 44;

        // Determine padding (how much larger than the element)
        let pad = Object.entries(spotlightPaddingMap || {}).find(([sel]) => {
          try { return !!document.querySelector(sel) && targetEl && targetEl.matches(sel); } catch { return false; }
        });
        pad = Array.isArray(pad) ? Number(pad[1]) : NaN;
        if (!Number.isFinite(pad)) pad = Number(defaultSpotlightPadding) || 0;

        // Compute viewport-relative rect and apply tight sizing
        const rect = targetEl.getBoundingClientRect();
        const top = Math.max(0, rect.top - pad);
        const left = Math.max(0, rect.left - pad);
        const width = Math.max(0, rect.width + pad * 2);
        const height = Math.max(0, rect.height + pad * 2);

        spotlightEl.style.borderRadius = `${radius}px`;
        spotlightEl.style.top = `${top}px`;
        spotlightEl.style.left = `${left}px`;
        spotlightEl.style.width = `${width}px`;
        spotlightEl.style.height = `${height}px`;
        return true;
      } catch {
        return false;
      }
    };

    const tryApplyWithRaf = (attempt = 0) => {
      if (cancelled) return;
      const ok = computeAndApply();
      if (!ok && attempt < 30) {
        rafId = requestAnimationFrame(() => tryApplyWithRaf(attempt + 1));
      }
    };

    // Initial attempt(s) via rAF frames (catch first mount of Joyride spotlight)
    tryApplyWithRaf(0);

    // Observe DOM for spotlight appearance/updates while this step is active
    try {
      observer = new MutationObserver(() => {
        // Re-apply whenever Joyride re-renders spotlight
        computeAndApply();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    } catch {}

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      try { observer && observer.disconnect(); } catch {}
    };
  }, [run, idx, joyrideSteps, spotlightRadiusMap, defaultSpotlightRadius, spotlightPaddingMap, defaultSpotlightPadding]);

  if (!isRoute) return null;

  return (
    <Joyride
      steps={joyrideSteps}
      run={run}
      stepIndex={idx}
      continuous
      hideBackButton
      hideCloseButton
      showProgress={false}
      showSkipButton={false}
      disableScrolling
      disableOverlayClose
      disableCloseOnEsc
      spotlightClicks
      scrollToFirstStep
      tooltipComponent={KeyboardTooltip}
      beaconComponent={NoBeacon}
      getHelpers={(h) => { helpersRef.current = h; try { if (run) h.go(idx || 0); } catch {} }}
      styles={{
        options: {
          zIndex,
          primaryColor,
          overlayColor: 'rgba(0,0,0,0.40)',
          spotlightPadding: 0,
        },
        tooltip: { borderRadius: 12 },
        // Spotlight styling: rounded highlight with subtle blue tint and glow
        spotlight: {
          borderRadius: 44,
          background: 'rgba(237, 241, 255, 0.20)',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.25), 0 0 30px 0 rgba(0,0,0,0.5)'
        },
      }}
      debug={debugTour}
      callback={(data) => {
        const { status, type, index } = data;
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
          setRun(false);
          setIdx(0);
          try {
            localStorage.setItem(TOUR_STORAGE_KEY, 'true');
            if (id === 'voice') {
              localStorage.setItem('voice_walkthrough_done', 'true');
              try { setVoiceWalkthrough(true); } catch {}
            } else if (id === 'keyboard') {
              localStorage.setItem('keyboard_walkthrough_done', 'true');
              try { setKeyboardWalkthrough(true); } catch {}
            }
            // Call the onTourComplete callback if provided
            if (typeof onTourComplete === 'function') {
              onTourComplete();
            }
          } catch {}
        } else if (type === 'target:notFound' || type === 'step:after') {
          setIdx((index || 0) + 1);
        }
      }}
    />
  );
}
